import { useEffect, useRef } from "react";

/** iOS-safe interval poller.
 *
 * The browser (especially iOS Safari in a regular tab) suspends background timers
 * and pending fetches when the tab is hidden. A naive `setInterval` then either
 * never fires again or fires with stuck closures, making the UI appear frozen.
 *
 * This hook:
 *   • Cancels any in-flight request via AbortController when the page hides.
 *   • Pauses the interval when document.hidden is true.
 *   • Fires `fn` immediately on visibilitychange/pageshow when the page returns.
 *   • Restarts the interval on resume so polling continues normally.
 *
 * @param {(signal: AbortSignal) => void | Promise<void>} fn — async polling callback. Will be
 *   re-invoked with a fresh AbortSignal every `intervalMs`.
 * @param {number} intervalMs — how often to poll while the page is visible.
 * @param {any[]} deps — dependency list (like useEffect).
 */
export function useSafePolling(fn, intervalMs = 5000, deps = []) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let timer = null;
    let controller = null;

    const fire = async () => {
      // Abort any prior in-flight request before starting a new one.
      controller?.abort();
      controller = new AbortController();
      try {
        await fnRef.current(controller.signal);
      } catch {
        // swallow — caller handles its own errors
      }
    };

    const start = () => {
      stop();
      fire();
      timer = setInterval(fire, intervalMs);
    };

    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
      controller?.abort();
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Page came back — fire immediately to recover from any stale state.
        start();
      }
    };

    // pageshow fires when the page is restored from BFCache (common on iOS).
    const onPageShow = (e) => {
      if (e.persisted || !timer) start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onPageShow);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
}
